import moment from 'moment-timezone';
import { Url } from '../../../../common/lang/web';
import { MonthOneBased } from '../../../../common/lang/dates';
import { Omit } from '../../../../common/lang/types';

/**
 * The supported media types. As of 2019-06-05 only 'photo' is supported.
 *
 * @see https://developers.google.com/my-business/reference/rest/v4/accounts.locations.media#mediaformat
 */
export enum MediaFormat {
    unspecified = 'MEDIA_FORMAT_UNSPECIFIED',
    photo = 'PHOTO',
    video = 'VIDEO',
}

/**
 * Represents a single media item.
 *
 * @see https://developers.google.com/my-business/reference/rest/v4/accounts.locations.media
 */
export interface PostMedia {
    mediaFormat: MediaFormat;
    sourceUrl: Url;
}

/**
 * @see https://developers.google.com/my-business/reference/rest/v4/accounts.locations.localPosts#LocalPostState
 */
export enum PostState {
    unspecified = 'LOCAL_POST_STATE_UNSPECIFIED',
    rejected = 'REJECTED',
    live = 'LIVE',
    processing = 'PROCESSING',
}

export function isPostState(value: unknown): value is PostState {
    return Object.values<unknown>(PostState).includes(value);
}

/**
 * @see https://developers.google.com/my-business/reference/rest/v4/accounts.locations.localPosts#LocalPostTopicType
 */
export enum PostTopicType {
    unspecified = 'LOCAL_POST_TOPIC_TYPE_UNSPECIFIED',
    standard = 'STANDARD',
    event = 'EVENT',
    offer = 'OFFER',
}

export function isPostTopicType(value: unknown): value is PostTopicType {
    return Object.values<unknown>(PostTopicType).includes(value);
}

export interface LocalPostEvent {
    title: string;
    schedule: {
        startDate: DateOnly;
        startTime: TimeOfDay;
        endDate: DateOnly;
        endTime: TimeOfDay;
    };
}

export type CallToAction = {
    actionType: CallToActionType;
    url?: Url;
};

export interface Offer {
    couponCode?: string;
    redeemOnlineUrl?: Url;
    termsConditions?: string;
}

export interface PublishedLocalPost {
    name: string;
    languageCode: string;
    state: PostState;
    updateTime: string;
    createTime: string;
    topicType: PostTopicType;
}

/**
 * @see https://developers.google.com/my-business/reference/rest/v4/accounts.locations
 */
export interface BasePublishableLocalPost {
    callToAction?: CallToAction;
    event?: LocalPostEvent;
    languageCode: string;
    media: PostMedia[];
    offer?: Offer;
    summary?: string;
    topicType: PostTopicType;
}

export type PublishableLocalOfferPost = Omit<BasePublishableLocalPost, 'callToAction'> & {
    event: LocalPostEvent;
    offer: Offer;
    topicType: PostTopicType.offer;
};

export type PublishableLocalEventPost = BasePublishableLocalPost & {
    event: LocalPostEvent;
    topicType: PostTopicType.event;
};

export type PublishableLocalPost = BasePublishableLocalPost | PublishableLocalEventPost | PublishableLocalOfferPost;

/**
 * The possible action types for a call to action post.
 *
 * @see https://developers.google.com/my-business/content/posts-data#action_types
 */
export enum CallToActionType {
    /**
     * Creates a Post that prompts a user to book an appointment, table, or something similar.
     */
    book = 'BOOK',
    /**
     * Creates a Post that prompts a user to order something.
     */
    order = 'ORDER',
    /**
     * Creates a Post that prompts a user to browse a product catalog.
     */
    shop = 'SHOP',
    /**
     * Creates a Post that prompts a user to see additional details on a website.
     */
    learnMore = 'LEARN_MORE',
    /**
     * Creates a Post that prompts a user to register, sign up, or join something.
     */
    signUp = 'SIGN_UP',
    /**
     * Creates a Post that prompts a user to call a business.
     */
    call = 'CALL',
}

type DateOnly = {
    year: number;
    month: MonthOneBased;
    day: number;
};

type TimeOfDay = {
    hours: number;
    minutes: number;
    seconds: number;
    nanos: number;
};

/**
 * Convert a date to a GMB compatible date parts in a given timezone.
 *
 * @param date - The date to convert
 * @param timezone - The timezone to convert to
 * @returns The converted GMB compatible date parts
 */
export function convertDateToDateParts(date: Date, timezone: string): [DateOnly, TimeOfDay] {
    const newDate = moment(date).tz(timezone);
    return [
        {
            day: newDate.date(),
            month: (newDate.month() + 1) as MonthOneBased,
            year: newDate.year(),
        },
        {
            hours: newDate.hours(),
            minutes: newDate.minutes(),
            nanos: 0,
            seconds: newDate.seconds(),
        },
    ];
}
